import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BrandPanel } from './Login';
import { passwordResetRequest } from '@/services/authClient';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) errs.email = 'Enter a valid email';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      await passwordResetRequest(email);
      setSent(true);
    } catch {
      setErrors({ api: 'Could not send reset email. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
        <BrandPanel />
        <div className="flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md text-center">
            <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-blue-50">
              <CheckCircle size={32} className="text-blue-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Check your email</h1>
            <p className="mt-2 text-slate-600">
              If an account exists for <strong>{email}</strong>, we sent a password reset link.
            </p>
            <p className="mt-4 text-sm text-slate-500">
              <Link to="/login" className="font-medium text-blue-600 hover:underline">Back to sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <BrandPanel />
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold text-slate-900">Forgot password</h1>
          <p className="mt-1 text-slate-600">Enter your email and we'll send you a reset link.</p>
          {errors.api && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{errors.api}</p>}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail size={16} className="absolute left-3 top-3 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-xl pl-9"
                />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={loading}>
              {loading ? 'Sending…' : 'Send reset link'}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-600">
            <Link to="/login" className="font-medium text-blue-600 hover:underline">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
